// Payment panel for the B2B wholesale screen.
// Supports cash / bank-transfer payment methods.
// Partial payment (tendered < total) requires a customer and a reason.
// After commit shows success state with print + new-order actions.

import { useState, useCallback } from "react";
import { Banknote, Building2, CheckCircle2, Printer, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MoneyInput } from "@/components/ui/money-input";
import { apiPost } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import type { Order, PaymentPart } from "@/features/pos/use-pos-cart";

interface Props {
  order: Order | null;
  workstationId: string | null;
  loading: boolean;
  onCommit: (params: {
    paymentMethod: string;
    tenderedAmount: number;
    /** Nhiều khoản trả trong 1 đơn (tiền mặt + CK). Có thì BE ưu tiên dùng. */
    payments?: PaymentPart[];
    reason?: string;
  }) => void;
  onNewOrder: () => void;
}

type Method = "cash" | "transfer" | "mixed";

function fmtVND(s: string) {
  const n = parseFloat(s);
  if (isNaN(n)) return "0";
  return new Intl.NumberFormat("vi-VN").format(n);
}

function parseVND(s: string) {
  return parseFloat(s.replace(/[^\d.]/g, "")) || 0;
}

export function WholesalePaymentPanel({ order, workstationId, loading, onCommit, onNewOrder }: Props) {
  const [method, setMethod] = useState<Method>("transfer");
  const [tenderedStr, setTenderedStr] = useState("");
  // Hỗn hợp: 2 ô riêng, tổng 2 ô = khách đã trả.
  const [mixedCashStr, setMixedCashStr] = useState("");
  const [mixedBankStr, setMixedBankStr] = useState("");
  const [reason, setReason] = useState("");
  const [printing, setPrinting] = useState(false);

  // Đơn trả thiếu thành partial_paid — vẫn là "đã xác nhận xong" với thu ngân.
  // Chỉ check "paid" từng làm màn thành công không hiện khi ghi nợ → NV tưởng
  // lỗi, bấm xác nhận lại nhiều lần và nhận 400.
  const isPaid = order?.status === "paid" || order?.status === "partial_paid";
  const total = parseVND(order?.total ?? "0");
  const isMixed = method === "mixed";
  const mixedCash = mixedCashStr.trim() ? parseVND(mixedCashStr) : 0;
  const mixedBank = mixedBankStr.trim() ? parseVND(mixedBankStr) : 0;
  // Một phương thức: để trống = trả đủ (quy ước cũ của màn sỉ).
  // Hỗn hợp: KHÔNG suy ra "trả đủ" — tổng 2 ô là con số duy nhất đáng tin,
  // để trống cả hai thì chặn xác nhận (nợ toàn bộ vẫn dùng 1 phương thức).
  const tendered = isMixed
    ? mixedCash + mixedBank
    : tenderedStr.trim()
      ? parseVND(tenderedStr)
      : total;
  // Trả thiếu (kể cả 0 = nợ toàn bộ) → ghi nợ. Chỉ cần chọn khách, lý do KHÔNG bắt buộc.
  const isDebt = tendered < total;
  const hasCustomer = !!order?.customerId;
  const debtAmount = Math.max(0, total - tendered);

  const canCommit =
    !isPaid &&
    !!order?.items?.length &&
    (!isDebt || hasCustomer) &&
    (!isMixed || tendered > 0);

  const handleCommit = useCallback(() => {
    if (!canCommit) return;
    const payments: PaymentPart[] = [];
    if (isMixed) {
      if (mixedCash > 0) payments.push({ method: "cash", amount: mixedCash });
      if (mixedBank > 0) payments.push({ method: "bank_manual", amount: mixedBank });
    }
    onCommit({
      paymentMethod: method === "transfer" ? "bank_manual" : "cash",
      tenderedAmount: tendered,
      payments: payments.length > 0 ? payments : undefined,
      reason: isDebt && reason.trim() ? reason.trim() : undefined,
    });
  }, [canCommit, method, isMixed, mixedCash, mixedBank, tendered, isDebt, reason, onCommit]);

  const handlePrint = useCallback(async () => {
    if (!order?.id) return;
    setPrinting(true);
    try {
      const url = `/v1/orders/${order.id}/print${workstationId ? `?workstation_id=${workstationId}` : ""}`;
      await apiPost(url);
      toast.success("Đã gửi lệnh in phiếu");
    } catch (err) {
      toast.error(translateApiError(err));
    } finally {
      setPrinting(false);
    }
    // TODO: dedicated A4/wholesale print template — deferred to post-MVP
  }, [order?.id, workstationId]);

  // Success state — after commit
  if (isPaid) {
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center px-4">
        <CheckCircle2 size={48} className="text-green-500" />
        <div>
          <p className="text-lg font-bold text-green-600">Đơn sỉ hoàn tất</p>
          <p className="text-sm text-muted-foreground">{order?.code}</p>
          {parseFloat(order?.debtAmount ?? "0") > 0 && (
            <p className="mt-1 text-sm font-medium text-amber-600">
              Ghi nợ: {fmtVND(order?.debtAmount ?? "0")} ₫
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={printing}
            className="gap-1.5"
          >
            <Printer size={14} />
            {printing ? "Đang in..." : "In phiếu"}
          </Button>
          <Button size="sm" onClick={onNewOrder} className="gap-1.5">
            <Plus size={14} />
            Đơn mới
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground px-4">
        Chưa có đơn. Thêm sản phẩm để bắt đầu.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Order totals */}
      <div className="rounded-lg border bg-muted/30 p-3 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Tạm tính</span>
          <span className="font-mono">{fmtVND(order.subtotal)} ₫</span>
        </div>
        {parseFloat(order.discountAmount) > 0 && (
          <div className="flex justify-between text-green-600">
            <span>Giảm giá</span>
            <span className="font-mono">−{fmtVND(order.discountAmount)} ₫</span>
          </div>
        )}
        <div className="flex justify-between font-semibold border-t pt-1 mt-1">
          <span>Tổng cộng</span>
          <span className="font-mono text-primary">{fmtVND(order.total)} ₫</span>
        </div>
      </div>

      {/* Payment method */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Hình thức TT</Label>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={() => setMethod("cash")}
            className={[
              "flex items-center justify-center gap-1.5 rounded-md border p-2.5 text-sm transition-colors",
              method === "cash"
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "hover:bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <Banknote size={15} />
            Tiền mặt
          </button>
          <button
            type="button"
            onClick={() => setMethod("transfer")}
            className={[
              "flex items-center justify-center gap-1.5 rounded-md border p-2.5 text-sm transition-colors",
              method === "transfer"
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "hover:bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <Building2 size={15} />
            CK
          </button>
          <button
            type="button"
            onClick={() => setMethod("mixed")}
            className={[
              "flex items-center justify-center gap-1.5 rounded-md border p-2.5 text-sm transition-colors",
              method === "mixed"
                ? "border-primary bg-primary/10 text-primary font-medium"
                : "hover:bg-muted text-muted-foreground",
            ].join(" ")}
          >
            <Banknote size={15} />
            Cả hai
          </button>
        </div>
      </div>

      {/* Tendered amount */}
      <div className="space-y-1">
        {isMixed ? (
          <>
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Khách trả — tiền mặt + chuyển khoản
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Tiền mặt</span>
                <MoneyInput
                  placeholder="0"
                  value={mixedCashStr}
                  onChange={setMixedCashStr}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs text-muted-foreground">Chuyển khoản</span>
                <MoneyInput
                  placeholder="0"
                  value={mixedBankStr}
                  onChange={setMixedBankStr}
                  className="font-mono"
                />
              </div>
            </div>
            {tendered === 0 && (
              <p className="text-xs text-muted-foreground">
                Nhập ít nhất một ô. Nợ toàn bộ thì chọn "Tiền mặt" hoặc "CK".
              </p>
            )}
          </>
        ) : (
          <>
            <Label htmlFor="tendered-input" className="text-xs text-muted-foreground uppercase tracking-wide">
              Khách trả
            </Label>
            <MoneyInput
              id="tendered-input"
              placeholder={fmtVND(order.total)}
              value={tenderedStr}
              onChange={setTenderedStr}
              className="font-mono"
            />
            {/* Nút nhanh — chị Lanh yêu cầu 26/07: khỏi phải gõ nguyên số tiền */}
            <div className="flex gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={() => setTenderedStr(String(Math.round(total)))}
              >
                Trả đủ {fmtVND(order.total)} ₫
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 text-xs"
                onClick={() => setTenderedStr("0")}
              >
                Nợ toàn bộ
              </Button>
            </div>
          </>
        )}

        {/* Tóm tắt LUÔN hiển thị — hết mơ hồ "để trống là gì" */}
        <div className={`rounded-md border px-3 py-2 text-sm ${isDebt ? (hasCustomer ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30" : "border-destructive/30 bg-destructive/5") : "border-green-300 bg-green-50 dark:bg-green-950/30"}`}>
          {isDebt ? (
            hasCustomer ? (
              <p className="text-amber-800 dark:text-amber-300 font-medium">
                Khách trả {fmtVND(String(tendered))} ₫ — Ghi nợ {fmtVND(String(debtAmount))} ₫
              </p>
            ) : (
              <p className="text-xs text-destructive">Chọn khách hàng để ghi nợ phần còn thiếu.</p>
            )
          ) : (
            <p className="text-green-700 dark:text-green-400 font-medium">
              ✓ Khách trả đủ {fmtVND(order.total)} ₫ — không ghi nợ
              {tendered > total && <> (thừa {fmtVND(String(tendered - total))} ₫)</>}
            </p>
          )}
        </div>
      </div>

      {/* Ghi chú nợ — không bắt buộc */}
      {isDebt && hasCustomer && (
        <div className="space-y-1">
          <Label htmlFor="reason-input" className="text-xs text-muted-foreground uppercase tracking-wide">
            Ghi chú nợ (không bắt buộc)
          </Label>
          <Input
            id="reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ví dụ: CK 50% còn lại sẽ trả sau 7 ngày..."
            className="text-sm"
          />
        </div>
      )}

      {/* Đơn tồn tại nhưng chưa có dòng hàng — trường hợp câm duy nhất còn lại
          của nút (các case thiếu khách/chưa nhập tiền đã có khối báo ở trên). */}
      {!order?.items?.length && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          Đơn chưa có sản phẩm nào — quét IMEI hoặc tìm sản phẩm để thêm trước khi xác nhận.
        </p>
      )}

      {/* Commit */}
      <Button
        className="w-full"
        size="lg"
        disabled={!canCommit || loading}
        onClick={handleCommit}
      >
        {loading ? "Đang xử lý..." : "Xác nhận đơn sỉ"}
      </Button>
    </div>
  );
}
